import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, OnDestroy, Service } from '@angular/core';
import { EMPTY, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { API_URLS } from '../constant/api-endpoint.constant';
import { LoginService, USE_BASE_USER_TOKEN } from '../authentication/login/login.service';
import { NotificationService, SKIP_ERROR_NOTIFICATION } from '../error/notification.service';
import { RestResponse } from '../model/base.service';
import { LocalStorageService } from '../model/local-storage.service';
import { UserPreference } from '../model/user-preference/user-preference.model';
import { mirrorBootPreferences } from './preferences.storage';
import { storageChanged$, takeRootEntries } from './storage';

/**
 * Keeps the localStorage user cache and the server in sync: pulled before the first authenticated
 * page renders, pushed back 3s after the last change, and once more before the tab goes away.
 *
 * Impersonating is read-only: the cache of the impersonated user is pulled, so the pages render as
 * they do for them, but it is served detached from the storage and never pushed back. Nothing of it
 * outlives the impersonation, and the cache of the signed-in user is left untouched throughout.
 *
 * A failed pull is not an empty server: nothing is pushed until one went through, so a transient
 * failure cannot replace the state of the server with the copy this machine happens to hold. A pull
 * that hangs counts as one: the guard waits on it, so it must never be the backend that decides how
 * long the first page takes to render.
 */
@Service()
export class UserPreferenceService implements OnDestroy {
  private static readonly PUSH_DEBOUNCE_MS = 3000;
  /** The pull holds the first render: a backend that goes quiet must not hold the app with it. */
  private static readonly PULL_TIMEOUT_MS = 5000;

  private readonly http = inject(HttpClient);
  private readonly storageService = inject(LocalStorageService);
  private readonly loginService = inject(LoginService);
  private readonly notificationService = inject(NotificationService);

  private readonly endpoint = `${environment.apiUrl}/${API_URLS.USER_PREFERENCE}`;
  private readonly destroy$ = new Subject<void>();
  private readonly hydratedSubject = new Subject<void>();

  /** Emits once the cache holds the server state, for what was read before it was known. */
  readonly hydrated$ = this.hydratedSubject.asObservable();

  private hydration: Observable<void>;
  private hydratedUserId: number;
  private lastPushedSnapshot: string = null;
  private pushErrorReported = false;
  /** Unset until a pull went through: what the server holds is unknown until then. */
  private serverStateKnown = false;
  private autoSyncSubscription: Subscription;

  constructor() {
    this.loginService.token$
      .pipe(
        filter(token => !token),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.reset());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoSync();
  }

  /**
   * Pulled once per signed-in user: the pages read the cache as they render, so it has to hold the
   * server state by then. Awaited by the guard, hence a completing observable even on failure.
   */
  hydrate(): Observable<void> {
    // The cache belongs to the impersonated user while impersonating, hence a pull of its own on
    // the way in and another one on the way out.
    const userId = this.loginService.currentUserValue?.id;
    if (userId == null) {
      return of(undefined);
    }
    if (this.hydration && this.hydratedUserId === userId) {
      return this.hydration;
    }

    this.hydratedUserId = userId;
    this.serverStateKnown = false;
    this.hydration = this.pull().pipe(
      timeout(UserPreferenceService.PULL_TIMEOUT_MS),
      map(response => response?.results ?? []),
      // Told apart from an empty server below, which the cache may seed and a failure may not. The
      // `TimeoutError` lands here too, a backend gone quiet being no more an empty one than a 500.
      catchError(() => of(null as UserPreference[])),
      switchMap(pulled => (pulled ? this.hydrateCache(pulled) : this.keepLocalCache())),
      tap(() => {
        // Started once the cache is settled, so it stands for what the server holds. Left
        // disarmed by a failed pull, hence a cache read but never pushed until the next one.
        this.startAutoSync();
        // Same reason: the mirror the next boot reads takes the settled theme and locale.
        mirrorBootPreferences();
        this.hydratedSubject.next();
      }),
      shareReplay(1)
    );

    return this.hydration;
  }

  /** Read as the current user: impersonating pulls the preferences of the impersonated one. */
  pull(): Observable<RestResponse<UserPreference>> {
    return this.http.get<RestResponse<UserPreference>>(this.endpoint, {
      context: preferenceContext(!this.loginService.isImpersonating),
    });
  }

  push(): Observable<UserPreference[]> {
    // The cache goes as a whole: pushing one the server never answered for would hand it a state
    // this machine alone knows, thinner than what it may already hold.
    if (
      this.loginService.isImpersonating ||
      !this.serverStateKnown ||
      !this.storageService.hasUserCache()
    ) {
      return of([]);
    }

    const snapshot = this.currentSnapshot();

    return this.http
      .post<UserPreference[]>(this.endpoint, this.preferences(), {
        context: preferenceContext(true),
      })
      .pipe(
        tap(() => (this.lastPushedSnapshot = snapshot)),
        catchError((error: HttpErrorResponse) => {
          // Reported once and never again, not even for the next user signing in: every column
          // added or removed pushes, and the same message on each of them says nothing more.
          if (!this.pushErrorReported) {
            this.pushErrorReported = true;
            this.notificationService.showError($localize`Error when updating user cache`);
          }

          return throwError(() => error);
        })
      );
  }

  /**
   * Closing the tab, reloading or navigating away leaves no time for an observable: `keepalive`
   * hands the request to the browser, which carries it out once the page is gone.
   */
  pushOnUnload(): void {
    const token = this.loginService.baseAccessToken;
    if (
      this.loginService.isImpersonating ||
      !token ||
      !this.serverStateKnown ||
      !this.storageService.hasUserCache() ||
      !this.isDirty()
    ) {
      return;
    }

    void fetch(this.endpoint, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(this.preferences()),
    }).catch(() => undefined);
  }

  /**
   * Cache pushed 3s after the last change, so a burst of writes costs one request. Idempotent: a
   * pull rewriting the cache calls it again to take the freshly pulled state as the baseline.
   */
  startAutoSync(): void {
    this.stopAutoSync();
    // Left disarmed while impersonating, and until a pull went through: what the pages write then
    // reaches no server at all.
    if (this.loginService.isImpersonating || !this.serverStateKnown) {
      return;
    }

    this.markSynced();
    this.autoSyncSubscription = storageChanged$
      .pipe(
        debounceTime(UserPreferenceService.PUSH_DEBOUNCE_MS),
        filter(() => this.isDirty()),
        switchMap(() => this.push().pipe(catchError(() => EMPTY)))
      )
      .subscribe();
  }

  stopAutoSync(): void {
    this.autoSyncSubscription?.unsubscribe();
    this.autoSyncSubscription = undefined;
  }

  /** Signing out stops the sync, the next user hydrating from the server again. */
  reset(): void {
    this.stopAutoSync();
    this.hydration = undefined;
    this.hydratedUserId = undefined;
    this.lastPushedSnapshot = null;
    this.serverStateKnown = false;
  }

  /**
   * What the server holds becomes the cache. Nothing there — a user it knows nothing about — means
   * the entries left at the root of the storage become its cache, sent back at once so the server
   * holds them too.
   */
  private hydrateCache(pulled: UserPreference[]): Observable<void> {
    this.serverStateKnown = true;
    // Impersonating: the detached cache takes what the server holds for the impersonated user and
    // nothing else. The root entries are left where they are, they belong to the signed-in one.
    if (this.loginService.isImpersonating) {
      this.storageService.setUserCache(this.storageService.deserialize(pulled));

      return of(undefined);
    }

    if (pulled.length) {
      this.storageService.setUserCache(this.storageService.deserialize(pulled));
      // Dropped: the cache holds the state now, so nothing would ever read them again.
      takeRootEntries();

      return of(undefined);
    }

    // Root entries first: a cache already there was written by a more recent version.
    const cache = { ...takeRootEntries(), ...this.storageService.getUserCache() };
    this.storageService.setUserCache(cache);

    if (!Object.keys(cache).length) {
      return of(undefined);
    }

    return this.push().pipe(
      catchError(() => of(null)),
      map(() => undefined)
    );
  }

  /**
   * Failed pull: the cache is served as it stands, so the pages render on the last state this
   * machine knew, and the hydration is forgotten so the next navigation pulls again. The root
   * entries are left where they are, the only copy of them until a pull answers for the server.
   */
  private keepLocalCache(): Observable<void> {
    this.hydration = undefined;
    this.hydratedUserId = undefined;

    return of(undefined);
  }

  private preferences(): UserPreference[] {
    return this.storageService.serialize(
      this.loginService.baseUser?.id,
      this.storageService.getUserCache()
    );
  }

  /** Serialized cache, to tell a write that changed nothing from one worth a request. */
  private currentSnapshot(): string {
    if (this.loginService.baseUser?.id == null) {
      return null;
    }

    return JSON.stringify(this.preferences());
  }

  private isDirty(): boolean {
    return this.currentSnapshot() !== this.lastPushedSnapshot;
  }

  private markSynced(): void {
    this.lastPushedSnapshot = this.currentSnapshot();
  }
}

/** Own message on failure, in place of the generic one the interceptor would show. */
function preferenceContext(useBaseUser: boolean): HttpContext {
  return new HttpContext().set(USE_BASE_USER_TOKEN, useBaseUser).set(SKIP_ERROR_NOTIFICATION, true);
}
